import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import activityLogService from '../services/activityLogService';

export const useActivityLogger = (customAlunoId = null) => {
  const { user } = useAuth();
  const alunoId = useRef(null);
  const pageLoadTime = useRef(Date.now());
  const lastInteraction = useRef(Date.now());

  // Atualizar ID do aluno quando disponível
  useEffect(() => {
    console.log('🔍 useActivityLogger: user data:', user);
    console.log('🔍 useActivityLogger: customAlunoId:', customAlunoId);
    
    // Priorizar customAlunoId se fornecido
    if (customAlunoId) {
      alunoId.current = customAlunoId;
      console.log('✅ alunoId definido via customAlunoId:', customAlunoId);
    } else if (user?.alunoId) {
      alunoId.current = user.alunoId;
      console.log('✅ alunoId definido via user.alunoId:', user.alunoId);
    } else {
      console.warn('⚠️ alunoId não encontrado. user:', user, 'customAlunoId:', customAlunoId);
    }
  }, [user, customAlunoId]);

  // Log de carregamento da página
  useEffect(() => {
    if (alunoId.current) {
      const loadDuration = Date.now() - pageLoadTime.current;
      activityLogService.logPerformance(alunoId.current, 'carregamento_pagina', {
        page: window.location.pathname,
        load_time: loadDuration
      }, loadDuration);
    }
  }, [alunoId.current]);

  // Detectar inatividade
  useEffect(() => {
    let inactivityTimer;
    const INACTIVITY_THRESHOLD = 30000; // 30 segundos

    const resetInactivityTimer = () => {
      lastInteraction.current = Date.now();
      clearTimeout(inactivityTimer);
      
      inactivityTimer = setTimeout(() => {
        if (alunoId.current) {
          activityLogService.logInteracao(alunoId.current, 'inatividade_detectada', {
            duracao_inatividade: INACTIVITY_THRESHOLD,
            ultima_interacao: new Date(lastInteraction.current).toISOString()
          });
        }
      }, INACTIVITY_THRESHOLD);
    };

    // Eventos que resetam o timer de inatividade
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, resetInactivityTimer, true);
    });

    resetInactivityTimer();

    return () => {
      clearTimeout(inactivityTimer);
      events.forEach(event => {
        document.removeEventListener(event, resetInactivityTimer, true);
      });
    };
  }, []);

  // Métodos de logging
  const logSessao = useCallback((tipo, detalhes = {}) => {
    if (alunoId.current) {
      return activityLogService.logSessao(alunoId.current, tipo, detalhes);
    }
  }, []);

  const logNavegacao = useCallback((tipo, detalhes = {}) => {
    if (alunoId.current) {
      return activityLogService.logNavegacao(alunoId.current, tipo, detalhes);
    }
  }, []);

  const logTeste = useCallback((tipo, detalhes = {}, duracao = null) => {
    if (alunoId.current) {
      return activityLogService.logTeste(alunoId.current, tipo, detalhes, duracao);
    }
  }, []);

  const logChat = useCallback((tipo, detalhes = {}, duracao = null) => {
    if (alunoId.current) {
      return activityLogService.logChat(alunoId.current, tipo, detalhes, duracao);
    }
  }, []);

  const logPerfil = useCallback((tipo, detalhes = {}) => {
    if (alunoId.current) {
      return activityLogService.logPerfil(alunoId.current, tipo, detalhes);
    }
  }, []);

  const logInteracao = useCallback((tipo, detalhes = {}) => {
    if (alunoId.current) {
      return activityLogService.logInteracao(alunoId.current, tipo, detalhes);
    }
  }, []);

  const logError = useCallback((error, contexto = {}) => {
    if (alunoId.current) {
      return activityLogService.logError(alunoId.current, error, contexto);
    }
  }, []);

  // Utilitários para medição de tempo
  const startTimer = useCallback((name) => {
    activityLogService.startTimer(name);
  }, []);

  const endTimer = useCallback((name) => {
    return activityLogService.endTimer(name);
  }, []);

  // Wrapper para capturar cliques automaticamente
  const trackClick = useCallback((elemento, detalhes = {}) => {
    return (event) => {
      logInteracao('clique_botao', {
        elemento: elemento,
        posicao_x: event.clientX,
        posicao_y: event.clientY,
        ...detalhes
      });
    };
  }, [logInteracao]);

  // Wrapper para capturar mudanças de foco
  const trackFocus = useCallback((campo, detalhes = {}) => {
    return () => {
      logInteracao('foco_campo', {
        campo: campo,
        ...detalhes
      });
    };
  }, [logInteracao]);

  // Wrapper para capturar scroll
  const trackScroll = useCallback((secao, detalhes = {}) => {
    return () => {
      logInteracao('scroll_pagina', {
        secao: secao,
        scroll_y: window.scrollY,
        scroll_percent: Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100),
        ...detalhes
      });
    };
  }, [logInteracao]);

  return {
    // Métodos de logging
    logSessao,
    logNavegacao,
    logTeste,
    logChat,
    logPerfil,
    logInteracao,
    logError,
    
    // Utilitários
    startTimer,
    endTimer,
    
    // Wrappers para eventos
    trackClick,
    trackFocus,
    trackScroll,
    
    // Estado
    isReady: !!alunoId.current
  };
};
